import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { DevTools } from "jotai-devtools"
import { NuqsAdapter } from "nuqs/adapters/react-router/v7"
import { BrowserRouter } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { getErrorMessage, isErrorToastHandled } from "@/api/instance"
import { AppRoutes } from "@/app/routes"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Toaster } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { customErrorMap } from "@/lib/zod-error-map"
import "jotai-devtools/styles.css"

// Zodのエラーメッセージを日本語化
z.setErrorMap(customErrorMap)

const showApiErrorToast = (error: unknown) => {
  if (isErrorToastHandled(error)) return
  toast.error("エラーが発生しました。", { description: getErrorMessage(error) })
}

const queryClient = new QueryClient({
  // 画面が onError を書いていれば、その画面が表示を担当する
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return
      showApiErrorToast(error)
    },
  }),
  // リトライし終えてから 1 回だけ出す。404 と、画面が meta.skipErrorToast で表示を引き受けたものは出さない
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.skipErrorToast) return
      if ((error as { status?: number }).status === 404) return
      showApiErrorToast(error)
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: (failureCount, error) => {
        // 401/403エラーの場合はリトライしない（api/instance.tsで処理済み）
        if (error instanceof Error) {
          interface ErrorWithStatus extends Error {
            status?: number
          }
          const errorData = error as ErrorWithStatus
          if (errorData.status === 401 || errorData.status === 403) {
            return false
          }
        }
        // それ以外は2回までリトライ
        return failureCount < 2
      },
      throwOnError: false, // エラーをスローしない（ErrorBoundaryではなくコンポーネント内でハンドリング）
    },
  },
})

function AppContent() {
  return (
    <BrowserRouter>
      <NuqsAdapter>
        <TooltipProvider>
          <AppRoutes />
          <Toaster />
        </TooltipProvider>
      </NuqsAdapter>
    </BrowserRouter>
  )
}

function App() {
  return (
    <ReactErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppContent />
        <ReactQueryDevtools initialIsOpen={false} />
        <DevTools />
      </QueryClientProvider>
    </ReactErrorBoundary>
  )
}

export default App
